import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    IconButton,
    CircularProgress,
    Typography,
    Tooltip,
} from '@mui/material';
import PropTypes from 'prop-types';
import { callBackend } from '../utils/api';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import { normalizeAumValue } from '../utils/aum';

export default function AumSelector({
    aum,
    refreshAum,
    hideTitle = false,
    compact = false,
    splitLayout = false,
    hideSaveButton = false,
    onDraftStateChange = null,
}) {
    const [value, setValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const normalizedAum = normalizeAumValue(aum);
    const formattedAum =
        normalizedAum != null
            ? normalizedAum.toLocaleString('en-US', { maximumFractionDigits: 0 })
            : '';

    useEffect(() => {
        setValue(formattedAum);
        setError('');
    }, [formattedAum]);

    const handleChange = e => {
        const raw = e.target.value.replace(/,/g, '').replace(/\D/g, '');
        setValue(raw ? Number(raw).toLocaleString('en-US') : '');
    };

    const handleBlur = () => {
        if (!value) {
            setValue(formattedAum);
        }
    };

    const handleSave = async () => {
        const numeric = parseInt(value.replace(/,/g, ''), 10);
        if (isNaN(numeric)) return;
        setLoading(true);
        try {
            await callBackend('set_aum', 'POST', { aum: numeric });
            setError('');
            refreshAum(numeric);
        } catch (err) {
            setError(`Problem setting AUM: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleUndo = () => {
        setValue(formattedAum);
        setError('');
    };

    const dirty = value !== formattedAum;
    const isSaveDisabled = loading || !value || !dirty;
    const numericDraftValue = (() => {
        const parsed = parseInt(String(value || '').replace(/,/g, ''), 10);
        return Number.isNaN(parsed) ? null : parsed;
    })();

    useEffect(() => {
        if (!onDraftStateChange) return;
        onDraftStateChange({
            numericValue: numericDraftValue,
            dirty,
            isValid: numericDraftValue != null,
            hasValue: Boolean(String(value || '').trim()),
        });
    }, [onDraftStateChange, numericDraftValue, dirty, value]);

    return (
         <>
            {!hideTitle && (
                <Typography
                    variant="subtitle1"
                    sx={{
                        mb: 1,
                        width: '100%',
                    }}
                >
                    AUM
                </Typography>
            )}
        {splitLayout ? (
            <>
                <TextField
                    size="small"
                    variant="outlined"
                    label="$"
                    value={value}
                    placeholder={formattedAum}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9,]*' }}
                    sx={{
                        maxWidth: '100%',
                        minWidth: 0,
                        width: '100%',
                        textAlign: 'center',
                        backgroundColor: dirty ? 'rgba(255, 215, 0, 0.2)' : 'transparent',
                    }}
                />
                <Box mt={compact ? 0 : 1} display="flex" gap={compact ? 0.5 : 1}>
                    {!hideSaveButton && (
                        <Tooltip title="Save">
                            <span>
                                <IconButton
                                    size="small"
                                    color="primary"
                                    aria-label="Save AUM"
                                    onClick={handleSave}
                                    disabled={isSaveDisabled}
                                >
                                    {loading ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                    <Tooltip title="Undo">
                        <span>
                            <IconButton
                                size="small"
                                color="error"
                                aria-label="Undo AUM change"
                                onClick={handleUndo}
                                disabled={!dirty || loading}
                            >
                                <UndoIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
                {error && (
                    <Typography color="error" variant="body2" sx={{ gridColumn: '1 / -1' }}>
                        {error}
                    </Typography>
                )}
            </>
        ) : (
            <Box
                sx={{
                    p: 0,
                    maxHeight: compact ? 72 : 120,
                    display: 'flex',
                    alignItems: 'center',
                    gap: compact ? 0.4 : 1,
                }}
            >
                <TextField
                    size="small"
                    variant="outlined"
                    label="$"
                    value={value}
                    placeholder={formattedAum}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9,]*' }}
                    sx={{
                        maxWidth: compact ? 138 : 120,
                        minWidth: compact ? 128 : 100,
                        textAlign: 'center',
                        backgroundColor: dirty ? 'rgba(255, 215, 0, 0.2)' : 'transparent',
                        '& .MuiInputLabel-root': {
                            fontSize: compact ? '0.72rem' : undefined,
                        },
                        '& .MuiOutlinedInput-root': {
                            minHeight: compact ? 30 : 40,
                            borderRadius: compact ? 999 : 1,
                            fontSize: compact ? '0.72rem' : undefined,
                        },
                        '& .MuiOutlinedInput-input': {
                            py: compact ? 0.55 : undefined,
                            px: compact ? 1.1 : undefined,
                        },
                    }}
                />
                <Box mt={compact ? 0 : 1} display="flex" gap={compact ? 0.5 : 1}>
                    {!hideSaveButton && (
                        <Tooltip title="Save">
                            <span>
                                <IconButton
                                    size="small"
                                    color="primary"
                                    aria-label="Save AUM"
                                    onClick={handleSave}
                                    disabled={isSaveDisabled}
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: compact ? 999 : 2,
                                        width: compact ? 28 : undefined,
                                        height: compact ? 28 : undefined,
                                    }}
                                >
                                    {loading ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                    <Tooltip title="Undo">
                        <span>
                            <IconButton
                                size="small"
                                color="error"
                                aria-label="Undo AUM change"
                                onClick={handleUndo}
                                disabled={!dirty || loading}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: compact ? 999 : 2,
                                    width: compact ? 28 : undefined,
                                    height: compact ? 28 : undefined,
                                }}
                            >
                                <UndoIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
                {error && (
                    <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                        {error}
                    </Typography>
                )}
            </Box>
        )}
        </>
    );
}

AumSelector.propTypes = {
    aum: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    refreshAum: PropTypes.func.isRequired,
    hideTitle: PropTypes.bool,
    compact: PropTypes.bool,
    splitLayout: PropTypes.bool,
    hideSaveButton: PropTypes.bool,
    onDraftStateChange: PropTypes.func,
};
